import { Injectable } from '@angular/core';
import { TimeZone } from '../model/timeZone';
import * as cityTimezones from 'city-timezones';
import { Country } from 'country-state-city';
import * as moment from 'moment-timezone';

@Injectable({
  providedIn: 'root'
})
export class TimezoneService {

  constructor() { }

  public getAllTimeZones(): TimeZone[] {
    let timeZonesList: TimeZone[] = [];
    Country.getAllCountries().forEach(country => {
      country.timezones.forEach(timezone => {
        const utc = `UTC${moment.tz(timezone.zoneName).format('Z')}`;
        // Formato exibido nos dropdowns: "(UTC-04:00) America/Toronto, Canada".
        // O `app-jems-select` das telas de evento passa `indicesFilter="filter"`
        // — ou seja, o texto mostrado nas opções vem DESTE campo (não `name`),
        // e ele é usado também na busca. Um valor só cobre exibição+match.
        const label = `(${utc}) ${timezone.zoneName}, ${country.name}`;
        const infoCountry: TimeZone = { name: country.name,
                                        timezone: timezone.zoneName,
                                        offset: timezone.gmtOffsetName,
                                        filter: label,
                                        label: label
                                      };
        timeZonesList.push(infoCountry);
      });
    });
    return timeZonesList;
  }

  public getTimeZone(city: string, state: string, country: string): string {
    // A lib `city-timezones` tem duas armadilhas confirmadas:
    //  1. `findFromCityStateProvince("Québec Canada")` retorna [] — ela matcha
    //     a string INTEIRA contra o campo `province`, que é só "Québec". Compound
    //     nunca casa. O código original chamava com "state country" e "city country"
    //     — ambos sempre falhavam pra qualquer país.
    //  2. `lookupViaCity("Montreal")` retorna [] — a lib guarda o nome com acento
    //     ("Montréal") no campo `city`; a chave ASCII fica em `city_ascii`. Se o
    //     usuário digitar sem acento, não casa.
    //
    // Estratégia: normalizar tudo pra ASCII lowercase e casar manualmente contra
    // `city_ascii`+`country`; fallback pra state+country se cidade não achar.
    const norm = (s: string) => (s || '')
      .normalize('NFD').replace(/[̀-ͯ]/g, '')   // strip acentos
      .toLowerCase().trim();
    const nCity = norm(city);
    const nState = norm(state);
    const nCountry = norm(country);
    const rows: any[] = (cityTimezones as any).cityMapping || [];

    let resolved = '';
    if (nCity && nCountry) {
      const hit = rows.find(r => norm(r.city_ascii) === nCity && norm(r.country) === nCountry);
      if (hit) { resolved = hit.timezone; }
    }
    if (!resolved && nState && nCountry) {
      const hit = rows.find(r => norm(r.province) === nState && norm(r.country) === nCountry);
      if (hit) { resolved = hit.timezone; }
    }
    if (!resolved && nCountry) {
      const hit = rows.find(r => norm(r.country) === nCountry);
      if (hit) { resolved = hit.timezone; }
    }
    if (!resolved) { return ''; }
    return this.canonicalizeTimeZone(resolved, country);
  }

  /** `city-timezones` devolve nomes IANA que podem estar DEPRECATED (ex.:
   *  `America/Montreal` foi unificado com `America/Toronto`). O dropdown vem
   *  de `country-state-city`, que só carrega os nomes canônicos modernos —
   *  então setar `America/Montreal` no form não seleciona nada. Aqui mapeamos
   *  o retorno da lib pra um nome que EXISTE no dropdown do mesmo país,
   *  preferindo um com o mesmo offset atual (Montreal −04 ⇒ Toronto −04). */
  private canonicalizeTimeZone(tz: string, countryName: string): string {
    const modern = Country.getAllCountries()
      .find(c => c.name === countryName)?.timezones?.map(t => t.zoneName) || [];
    if (modern.includes(tz)) { return tz; }   // já é canônico
    if (!modern.length) { return tz; }        // sem alternativas conhecidas
    // ICU do navegador resolve nomes IANA deprecated pro canônico atual:
    // `America/Montreal` → `America/Toronto`, `Asia/Calcutta` → `Asia/Kolkata`,
    // etc. Heurística por offset não serve porque múltiplas zonas do mesmo
    // país compartilham offset+DST (Iqaluit também é Eastern Time canadense).
    try {
      const canonical = new Intl.DateTimeFormat('en-US', { timeZone: tz })
        .resolvedOptions().timeZone;
      if (canonical && modern.includes(canonical)) { return canonical; }
    } catch { /* fallback abaixo */ }
    // Último recurso: primeira zona do país com o mesmo perfil jan+jul.
    const jan = (z: string) => moment.tz('2024-01-15', z).utcOffset();
    const jul = (z: string) => moment.tz('2024-07-15', z).utcOffset();
    const tJan = jan(tz), tJul = jul(tz);
    return modern.find(z => jan(z) === tJan && jul(z) === tJul) || modern[0];
  }

  public convertTimeToBrowserTimezone(time: Date, timeZone: string, utcOffset?: number): Date {
    /*
    * This function is necessary because the time is save in the database in relation to the browser's time zone.
    */
    const offsetTimeZone: number = timeZone ? moment.tz(timeZone).utcOffset() : utcOffset;
    const offsetBrowserTimeZone: number = moment().utcOffset();
    let offset: number;

    if (offsetTimeZone >= 0) {
      offset = offsetBrowserTimeZone + offsetBrowserTimeZone - offsetTimeZone;
    } else {
      const x = offsetBrowserTimeZone + (offsetBrowserTimeZone + 60); 
      const y = offsetTimeZone + 60;
      offset = x - y;
    }

    return this.timeZoneConverter(time, offset);
  }

  public timeZoneConverter(date: Date, offset: number): Date {
    return new Date(date.setMinutes(date.getMinutes() + date.getTimezoneOffset() + offset));
  }

  public getOffsetBrowserTimeZone(type: string = 'hours'): number {
    if (type === 'minutes') {
      return moment().utcOffset();
    } else {
      return moment().utcOffset() / 60;
    }
  }

  public get browserTimeZone(): string {
    return moment.tz.guess(true);
  }

  public getUTCTimeZone(timeZone: string): string {
    return moment.tz(timeZone).format('Z');
  }
}
